import { Flex, Loader, Switch, Skeleton, Text } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import {
  type ParameterID,
  type CoreInsurance,
  type CoreInsuranceQuoteResponseList,
} from '@flecto/types'
import {
  CollapsibleCard,
  type CollapsibleCardProps,
  iconInsuranceOn,
  iconInsuranceOff,
  TextCurrency,
} from '@flecto/ui'

import { useBookingInsuranceInfo } from '../../hooks/useBookingInsuranceInfo'
import { useBookingInsuranceToggleStatus } from '../../hooks/useBookingInsuranceToggleStatus'
import styles from './styles.module.css'

type CardBookingInsuranceEstimatedCost = CollapsibleCardProps & {
  bookingId: ParameterID
  quotes?: CoreInsuranceQuoteResponseList
  isLoadingQuotes?: boolean
}

export function CardBookingInsuranceEstimatedCost({
  bookingId,
  quotes,
  isLoadingQuotes,
  ...props
}: CardBookingInsuranceEstimatedCost) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_insurances' })

  const {
    isToggling,
    isBookingInsuranceEnabled,
    toggleBookingInsuranceStatus,
  } = useBookingInsuranceToggleStatus({ bookingId })

  const { isAccountInsuranceEnabled } = useBookingInsuranceInfo({ bookingId })

  const hasNoQuotes = !quotes?.insurance_quote?.length || !quotes

  if (!isAccountInsuranceEnabled) {
    return null
  }

  const color = isBookingInsuranceEnabled ? 'brand' : 'gray.7'

  return (
    <CollapsibleCard {...props} preventOpen={hasNoQuotes}>
      <CollapsibleCard.Title>
        <Flex align='center' gap='sm' wrap='wrap'>
          {t('card_estimated_cost_title')}
          {isLoadingQuotes ? (
            <Skeleton width={50} height={10} />
          ) : (
            <TextCurrency cents={quotes?.quote_total_cents ?? 0} c={color} />
          )}
        </Flex>
      </CollapsibleCard.Title>
      <CollapsibleCard.Description>
        {isBookingInsuranceEnabled ? (
          t('card_estimated_cost_description_enabled')
        ) : (
          <Text c='orange'>
            {t('card_estimated_cost_description_disabled')}
          </Text>
        )}
      </CollapsibleCard.Description>
      <CollapsibleCard.Icon
        icon={isBookingInsuranceEnabled ? iconInsuranceOn : iconInsuranceOff}
        color={color}
        withCircleBackground
        style={{ flexShrink: 0 }}
      />
      <CollapsibleCard.RightSection>
        <Switch
          disabled={isToggling || hasNoQuotes}
          checked={isBookingInsuranceEnabled}
          onClick={toggleBookingInsuranceStatus}
          thumbIcon={isToggling && <Loader size='xs' mt={-3} />}
        />
      </CollapsibleCard.RightSection>
      <CollapsibleCard.Content>
        {isLoadingQuotes && (
          <Flex direction='column' gap='sm'>
            <Skeleton height={16} width='100%' radius='xl' />
            <Skeleton height={16} width='100%' radius='xl' />
            <Skeleton height={16} width='100%' radius='xl' />
          </Flex>
        )}
        {(quotes?.insurance_quote ?? []).map(
          (insurance: CoreInsurance, key: number) => {
            return (
              <Flex
                key={`insurance-quote-${key}`}
                gap='sm'
                justify='space-between'
                className={styles.quoteItem}
              >
                <Text truncate>{insurance.item?.title}</Text>
                <TextCurrency
                  cents={insurance.price_cents}
                  ta='right'
                  style={{ flexShrink: 0 }}
                />
              </Flex>
            )
          }
        )}
      </CollapsibleCard.Content>
    </CollapsibleCard>
  )
}
